import { useLayoutEffect, useRef, useState } from 'react'
import { getShapePath } from '@/lib/boundaries/shape-boundary'
import type { ShapeData } from '@/lib/shapes/types/shape-data'
import { fallbackShapePathD, resolveShapePathD } from '../helpers/shape-path-resolution'

/**
 * The SVG `d` a shape draws. Data the shape already carries resolves synchronously
 * (`resolveShapePathD`); otherwise the WASM generator runs, and only its latest request wins.
 * Any failure falls back to a plain rectangle.
 */
export function useShapePath(shape: ShapeData): string {
  const [shapePath, setShapePath] = useState('')
  const requestIdRef = useRef(0)

  // useLayoutEffect so synchronous path sources (shape.path, pathCommands) update before the
  // browser paints, avoiding a visible frame with stale path data.
  /* eslint-disable react-hooks/exhaustive-deps -- `shape` is intentionally omitted: deps are
   * path-affecting fields only so the effect does not re-run on x/y/opacity or new object
   * identity; listing `shape` would satisfy the rule but re-run on every position tweak. */
  useLayoutEffect(() => {
    const requestId = ++requestIdRef.current
    const applyIfLatest = (d: string) => {
      if (requestId === requestIdRef.current) {
        setShapePath(d)
      }
    }

    const loadShapePath = async () => {
      try {
        applyIfLatest(
          resolveShapePathD(shape) ?? (await getShapePath(shape.type, shape.width, shape.height))
        )
      } catch (error) {
        console.error('Failed to load shape path:', error)
        applyIfLatest(fallbackShapePathD(shape.width, shape.height))
      }
    }

    loadShapePath()
  }, [
    shape.type,
    shape.width,
    shape.height,
    shape.path,
    shape.pathCommands,
    shape.kindId,
    shape.roundedCorner,
    shape.sides,
  ])
  /* eslint-enable react-hooks/exhaustive-deps */

  return shapePath
}
